import React, { useEffect, useCallback } from 'react';

import theme from 'theme';
import i18n from 'i18n-js';
import { Card } from 'components/UI';
import { UserContext } from 'context';
import { FETCH_STATE } from 'constants';
import StatisticBox from './StatisticBox';
import InfoIcon from '@mui/icons-material/Info';
import { useDispatch, useSelector } from 'react-redux';
import PendingReservationsBanner from './PendingReservationsBanner';
import { DashboardActions } from 'state/modules/dashboard';
import { Grid, Box, Typography, Tooltip } from '@mui/material';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const Statistics = () => {
  const dispatch = useDispatch();
  const { orgType } = React.useContext(UserContext);

  const reservationStats = useSelector((state) => state.dashboard.reservationStats);
  const vehicleStats = useSelector((state) => state.dashboard.vehicleStats);
  const fetchingReservationStats = useSelector((state) => state.dashboard.fetchingReservationStats);
  const fetchingVehicleStats = useSelector((state) => state.dashboard.fetchingVehicleStats);

  const getStats = useCallback(() => {
    if (orgType !== 'msOwnerOrg') {
      dispatch(DashboardActions.fetchReservationStats());
    }
    dispatch(DashboardActions.fetchVehicleStats());
  }, [orgType, dispatch]);

  useEffect(() => {
    orgType && getStats();
  }, [orgType, getStats]);

  const loadingStats =
    fetchingReservationStats == FETCH_STATE.LOADING || fetchingVehicleStats == FETCH_STATE.LOADING;

  const isPendingFeaturesEnabled = useFeatureIsOn('reservations_pending_behaviors');
  return (
    <Box>
      {isPendingFeaturesEnabled && <PendingReservationsBanner />}
      <Card
        enableCollapse
        id="statistics-widget"
        loading={loadingStats}
        headerActionType="vertical"
        onRefresh={() => getStats()}
        title={i18n.t(
          `dashboard.${orgType !== 'msOwnerOrg' ? 'vehicles_reservations' : 'vehicles'}`
        )}
        cardProps={{
          style: {
            borderRadius: isPendingFeaturesEnabled ? '0 0 8px 8px' : '8px',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid
              size={{
                xs: 12,
                md: orgType !== 'msOwnerOrg' ? 6 : 12,
                lg: orgType !== 'msOwnerOrg' ? 8 : 12,
              }}
            >
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid size={12}>
                  <Box
                    paddingX={2}
                    paddingY={1}
                    sx={{ backgroundColor: 'general.extraLightGreen', borderRadius: '5px' }}
                  >
                    <Typography>
                      <Box
                        display="flex"
                        component="span"
                        alignItems="center"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: 'general.green',
                          textTransform: 'uppercase',
                        }}
                      >
                        {i18n.t('common.vehicles')}
                        <Tooltip
                          title={i18n.t('dashboard.statistics.vehicles_description')}
                          placement="right"
                        >
                          <InfoIcon
                            sx={{ fontSize: '15px', color: 'general.green', marginLeft: '4px' }}
                          />
                        </Tooltip>
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <StatisticBox
                    id="active"
                    count={vehicleStats?.active || 0}
                    color={theme.palette.general.green}
                    label={i18n.t(`common.active`)}
                    link="/vehicles?status_filter=only&statuses=ACTIVE"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <StatisticBox
                    id="inshop"
                    count={vehicleStats?.inshop || 0}
                    color={theme.palette.general.red}
                    label={i18n.t(`common.in_shop`)}
                    link="/vehicles?status_filter=only&statuses=INSHOP"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <StatisticBox
                    id="inactive"
                    count={vehicleStats?.inactive || 0}
                    color={theme.palette.general.grey}
                    label={i18n.t(`common.inactive`)}
                    link="/vehicles?status_filter=only&statuses=INACTIVE"
                  />
                </Grid>
              </Grid>
            </Grid>
            {orgType && orgType !== 'msOwnerOrg' && (
              <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                  <Grid size={12}>
                    <Box
                      paddingX={2}
                      paddingY={1}
                      sx={{ backgroundColor: 'general.extraLightBlue', borderRadius: '5px' }}
                    >
                      <Typography>
                        <Box
                          display="flex"
                          alignItems="center"
                          component="span"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: 'general.blue',
                            textTransform: 'uppercase',
                          }}
                        >
                          {i18n.t('common.reservations')}
                          <Tooltip
                            title={i18n.t('dashboard.statistics.reservations_description')}
                            placement="right"
                          >
                            <InfoIcon
                              sx={{ fontSize: '15px', color: 'general.blue', marginLeft: '4px' }}
                            />
                          </Tooltip>
                        </Box>
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <StatisticBox
                      id="reserved"
                      count={reservationStats?.reserved || 0}
                      color={theme.palette.general.lightBlue}
                      label={i18n.t(`common.reserved`)}
                      link="/reservations?statuses=RESERVED"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <StatisticBox
                      id="inprogress"
                      count={reservationStats?.inprogress || 0}
                      color={theme.palette.general.lightBlue}
                      label={i18n.t(`common.inprogress`)}
                      link="/reservations?statuses=INPROGRESS"
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>
    </Box>
  );
};

export default Statistics;
